import React, { useEffect, useState } from "react";
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import { Visibility, Delete, Edit, Search, Add } from "@mui/icons-material";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  listRooms,
  createRoom,
  updateRoom,
  deleteRoom,
  listWards,
} from "../../../api/bedManagement";

const RoomList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRooms, setTotalRooms] = useState(0);

  // Debounce search query to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Rooms with Pagination
  const { data, refetch, isLoading, isError } = useQuery({
    queryKey: [
      "rooms-list",
      { search: debouncedSearch, page, page_size: rowsPerPage },
    ],
    queryFn: () =>
      listRooms({ search: debouncedSearch || undefined, page, page_size: rowsPerPage }, token),
  });

  // Fetch Wards for dropdown - optimized to fetch only first 100
  const { data: wardsData } = useQuery({
    queryKey: ["wards-dropdown"],
    queryFn: () => listWards({ page_size: 100 }, token),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Calculate total rooms from response
  useEffect(() => {
    if (data) {
      const total = Array.isArray(data) ? data.length : data?.count || 0;
      setTotalRooms(total);
    }
  }, [data]);

  // Dialog and Snackbar States
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    room_number: "",
    room_type: 1,
    ward: "",
    status: true,
    daily_rate: "",
  });

  // Handlers for Update Dialog
  const handleOpenUpdateDialog = (room) => {
    setSelectedRoom(room);
    setForm({
      name: room ? room.name : "",
      room_number: room ? room.room_number : "",
      room_type: room ? room.room_type : 1,
      ward: room ? room.ward : "",
      status: room ? room.status : true,
      daily_rate: room ? room.daily_rate || "" : "",
    });
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedRoom(null);
    setForm({
      name: "",
      room_number: "",
      room_type: 1,
      ward: "",
      status: true,
      daily_rate: "",
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedRoom) {
        await updateRoom(selectedRoom.id, form, token);
        setSnackbarMessage(
          t("bedManagement.room_updated", {
            defaultValue: "Room updated successfully",
          })
        );
      } else {
        await createRoom(form, token);
        setSnackbarMessage(
          t("bedManagement.room_created", {
            defaultValue: "Room created successfully",
          })
        );
      }
      refetch();
      handleCloseUpdateDialog();
    } catch (error) {
      console.error("Error saving room:", error);
      setSnackbarMessage(
        t("bedManagement.error_saving", { defaultValue: "Error saving room" })
      );
    }
  };

  const handleDelete = async (roomId) => {
    try {
      await deleteRoom(roomId, token);
      setSnackbarMessage(
        t("bedManagement.room_deleted", {
          defaultValue: "Room deleted successfully",
        })
      );
      refetch();
    } catch (error) {
      console.error("Error deleting room:", error);
      setSnackbarMessage(
        t("bedManagement.error_deleting", {
          defaultValue: "Error deleting room",
        })
      );
    }
  };



  const getRoomTypeLabel = (type) => {
    switch (type) {
      case 1:
        return t("bedManagement.general", { defaultValue: "General" });
      case 2:
        return t("bedManagement.private", { defaultValue: "Private" });
      case 3:
        return t("bedManagement.vip", { defaultValue: "VIP" });
      default:
        return "";
    }
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "none", m: "1em 3em" }}>
        <Grid container padding={"20px"}>
          {/* Header Section with Search Bar */}
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {t("bedManagement.rooms.title", {
                defaultValue: "Rooms Management",
              })}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenUpdateDialog(null)}
                sx={{ textTransform: "capitalize" }}
              >
                {t("bedManagement.add_room", { defaultValue: "Add Room" })}
              </Button>
              <TextField
                placeholder={t("bedManagement.search", {
                  defaultValue: "Search rooms...",
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "300px" }}
              />
            </Box>
          </Grid>
          <Divider sx={{ mt: 1, width: "100%" }} />

          {/* Loading, Error, or Data Display */}
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : isError ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <Typography color="error">
                {t("bedManagement.failedToLoad", {
                  defaultValue: "Failed to load rooms",
                })}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => refetch()}
                sx={{ mt: 2 }}
              >
                {t("bedManagement.retry", { defaultValue: "Retry" })}
              </Button>
            </Grid>
          ) : (
            <>
              <TableContainer sx={{ mt: 4 }}>
                <Table sx={{ minWidth: "100%" }} aria-label="rooms table">
                  <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell align="center">
                        {t("bedManagement.rooms.id", { defaultValue: "ID" })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.name", {
                          defaultValue: "Name",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.room_number", {
                          defaultValue: "Room Number",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.ward", {
                          defaultValue: "Ward",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.room_type", {
                          defaultValue: "Type",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.daily_rate", {
                          defaultValue: "Daily Rate",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.rooms.status", {
                          defaultValue: "Status",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.actions", {
                          defaultValue: "Actions",
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {(Array.isArray(data) ? data : data?.results || [])?.length > 0 ? (
                      (Array.isArray(data) ? data : data?.results || []).map((room) => (
                        <TableRow
                          key={room.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">{room.id}</TableCell>
                          <TableCell align="center">{room.name}</TableCell>
                          <TableCell align="center">
                            {room.room_number}
                          </TableCell>
                          <TableCell align="center">{room.ward_name}</TableCell>
                          <TableCell align="center">
                            {getRoomTypeLabel(room.room_type)}
                          </TableCell>
                          <TableCell align="center">
                            {room.daily_rate
                              ? `${room.daily_rate} AFN`
                              : t("bedManagement.not_set", {
                                  defaultValue: "Not Set",
                                })}
                          </TableCell>
                          <TableCell align="center">
                            {room.status
                              ? t("bedManagement.active", {
                                  defaultValue: "Active",
                                })
                              : t("bedManagement.inactive", {
                                  defaultValue: "Inactive",
                                })}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleOpenUpdateDialog(room)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(room.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t("bedManagement.no_rooms", {
                              defaultValue: "No rooms found",
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
                flexWrap="wrap"
                gap={2}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {t("bedManagement.rows_per_page", "Rows Per Page")}:
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                {totalRooms > rowsPerPage && (
                  <Pagination
                    count={Math.ceil(totalRooms / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                )}
              </Box>
            </>
          )}
        </Grid>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedRoom
            ? t("bedManagement.rooms.edit", { defaultValue: "Edit Room" })
            : t("bedManagement.rooms.add", { defaultValue: "Add Room" })}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.rooms.name", {
                    defaultValue: "Room Name",
                  })}
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.rooms.room_number", {
                    defaultValue: "Room Number",
                  })}
                  value={form.room_number}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, room_number: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    value={form.ward}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, ward: e.target.value }))
                    }
                    displayEmpty
                    required
                  >
                    <MenuItem value="" disabled>
                      {t("bedManagement.select_ward", {
                        defaultValue: "Select Ward",
                      })}
                    </MenuItem>
                    {(Array.isArray(wardsData) ? wardsData : wardsData?.results || [])?.map((ward) => (
                      <MenuItem key={ward.id} value={ward.id}>
                        {ward.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    value={form.room_type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, room_type: e.target.value }))
                    }
                    required
                  >
                    <MenuItem value={1}>
                      {t("bedManagement.general", { defaultValue: "General" })}
                    </MenuItem>
                    <MenuItem value={2}>
                      {t("bedManagement.private", { defaultValue: "Private" })}
                    </MenuItem>
                    <MenuItem value={3}>
                      {t("bedManagement.vip", { defaultValue: "VIP" })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.rooms.daily_rate", {
                    defaultValue: "Daily Rate (per 24 hours) - AFN",
                  })}
                  type="number"
                  value={form.daily_rate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, daily_rate: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={t("bedManagement.rooms.daily_rate_helper", {
                    defaultValue: "Charge per day for this room",
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value }))
                    }
                    required
                  >
                    <MenuItem value={true}>
                      {t("bedManagement.active", { defaultValue: "Active" })}
                    </MenuItem>
                    <MenuItem value={false}>
                      {t("bedManagement.inactive", {
                        defaultValue: "Inactive",
                      })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpdateDialog}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" variant="contained">
              {selectedRoom
                ? t("common.update", { defaultValue: "Update" })
                : t("common.create", { defaultValue: "Create" })}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
    </Grid>
  );
};

export default RoomList;
